import * as moment from 'moment';

import { AccountOrganization, Organization } from '../shared/sdk';
import {
  AccountPaymentProcessingControllerService,
  AccountStripeConnectControllerService,
} from '../shared/api/services';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  LanguageService,
  languages,
} from '../shared/services/language.service';
import {
  Observable,
  ReplaySubject,
  Subject,
  combineLatest,
  of,
  zip,
} from 'rxjs';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import {
  catchError,
  delay,
  filter,
  map,
  shareReplay,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators';

import { AccountService } from '../accounts/account.service';
import { ContentfulService } from '../shared/services/contentful.service';
import { FeatureModalService } from '../feature-modal/feature-modal.service';
import { GlobalSubscriptionService } from '../subscriptions/global-subscription.service';
import { HelpModalService } from '../help-modal/help-modal.service';
import { OrderService } from '../orders/order.service';
import { OrganizationService } from '../organizations/organization.service';
import { SDKService } from '../sdk.service';
import { Sidebar2Service } from './sidebar2.service';
import { SidebarItem } from './sidebar-item/sidebar-item.component';
import { StorageService } from '../shared/services/storage.service';
import { WarehouseService } from '../warehouses/warehouse.service';
import { WhiteLabelService } from '../shared/services/whitelabel.service';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-sidebar2',
  templateUrl: './sidebar2.component.html',
  styleUrls: ['./sidebar2.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('mobileVisibilityState', [
      state(
        'hidden',
        style({
          left: '-64px',
        }),
        { params: { screenHeight: 0 } },
      ),
      state(
        'visible',
        style({
          top: '0px',
        }),
      ),
      transition('hidden => visible', animate('120ms ease-in')),
      transition('visible => hidden', animate('200ms ease-out')),
    ]),
  ],
})
export class Sidebar2Component implements OnInit, OnDestroy {
  selfUrl = environment.selfUrl;
  hasIntegrations: Observable<boolean>;
  hasUnits: Observable<boolean>;
  hasInventory: Observable<boolean>;
  hideSidebar: Observable<boolean>;
  loggingOut = new ReplaySubject<boolean>(1);
  items: SidebarItem[];
  items$: Observable<SidebarItem[]>;
  lastClicked: string;
  mobileVisibilityState = 'visible';
  mobile = false;
  private onDestroy = new Subject<void>();
  private whatsNewCookieKey = 'Apollo::new:vAt';

  @HostListener('window:resize', ['$event']) onResizeEvent(): void {
    this.onResize();
  }

  constructor(
    public wls: WhiteLabelService,
    private accountService: AccountService,
    private cdr: ChangeDetectorRef,
    private contentfuService: ContentfulService,
    private featureModalService: FeatureModalService,
    private globalSubscriptionService: GlobalSubscriptionService,
    private helpModalService: HelpModalService,
    private languageService: LanguageService,
    private orderService: OrderService,
    private orgService: OrganizationService,
    private route: ActivatedRoute,
    private router: Router,
    private sdkService: SDKService,
    private sidebarService: Sidebar2Service,
    private accountStripeConnectService: AccountStripeConnectControllerService,
    private accountPaymentProcessingService: AccountPaymentProcessingControllerService,
    private storateService: StorageService,
    private warehouseService: WarehouseService,
  ) {}

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  ngOnInit(): void {
    this.hideSidebar = this.sdkService.isSideMenuShown$.pipe(
      map((isShown) => !isShown),
      takeUntil(this.onDestroy),
    );

    this.loggingOut.next(false);

    this.hasUnits = this.globalSubscriptionService.has(
      'units',
    ) as Observable<boolean>;
    this.hasIntegrations = this.globalSubscriptionService.has(
      'integrations',
    ) as Observable<boolean>;
    this.hasInventory = this.globalSubscriptionService.has(
      'inventory',
    ) as Observable<boolean>;

    const activeOrganizations: Observable<Organization[]> =
      this.accountService.active.pipe(
        filter((account) => !!account),
        switchMap((account) =>
          this.orgService.get(account.id, {
            include: {
              relation: 'accountOrganizations',
              scope: {
                where: { accountId: account.id },
              },
            },
            sort: 'name ASC',
            limit: 10,
          }),
        ),
        map(({ body }) => body),
        map((organizations) =>
          organizations.filter(
            (o) => o.accountOrganizations && o.accountOrganizations[0].active,
          ),
        ),
        shareReplay(1),
      );

    const wl = combineLatest([this.wls.isWhiteLabel, this.wls.properties]);

    const notWLnotSI: Observable<boolean> = wl.pipe(
      map(([isWL]) => !isWL),
      switchMap((notWL) =>
        this.globalSubscriptionService.active.pipe(
          map((subs) => notWL && subs.type !== 'SI'),
        ),
      ),
    );

    const isAdmin: Observable<boolean> = this.accountService.is([
      'admin',
      'superadmin',
    ]);

    const stripeConnectActive = this.accountStripeConnectService
      .get({ filter: { where: { active: true } } })
      .pipe(
        map((stripeConnect) => !!stripeConnect),
        catchError((err) => {
          if (err.status === 404) return of(false);
          throw err;
        }),
        shareReplay(1),
      );

    const paymentProcessingActive = this.accountPaymentProcessingService
      .get({ filter: { where: { active: true } } })
      .pipe(
        map((paymentProcessing) => !!paymentProcessing),
        catchError((err) => {
          if (err.status === 404) return of(false);
          throw err;
        }),
        shareReplay(1),
      );

    this.items = [
      // Whitelabel - Logo & Dashboard link
      {
        name: '',
        icon: '',
        img: this.wls.properties.pipe(map(({ logo }) => logo)),
        fn: () => this.onOpen(['dashboard']),
        visible: wl.pipe(
          map(
            ([isWL, { logo }]) =>
              isWL && logo && !this.wls.getI('sidebar.dashboard.hidden'),
          ),
        ),
      },
      // Whitelabel - Snapguest
      {
        name: 'sidebar.guestbook.title',
        icon: 'fe fe-book-open',
        fn: () => this.onOpen(['guestbook']),
        visible: wl.pipe(
          map(([isWL, { name }]) => isWL && name === 'Snapguest'),
        ),
      },
      // Not white label
      {
        name: 'sidebar.dashboard.title',
        icon: 'fe fe-triangle',
        fn: () => this.onOpen(['dashboard']),
        visible: wl.pipe(map(([isWL]) => !isWL)),
      },
      // Whitelabel - Separate dashboard icon
      {
        name: 'sidebar.dashboard.title',
        icon: 'fe fe-home',
        fn: () => this.onOpen(['dashboard']),
        visible: wl.pipe(
          switchMap(([isWL]) => {
            if (!isWL) return of(false);

            return this.wls.properties.pipe(
              map(({ separateDashboardIcon }) => separateDashboardIcon),
            );
          }),
        ),
      },

      // Top level invoice item (wl opt-in only)
      {
        name: 'sidebar.documents.outgoing.invoices.title',
        icon: 'fe fe-file',
        fn: () => this.onOpen(['documents/o/invoice']),
        visible: of(this.wls.getI('sidebar.invoice.visible')),
      },
      // Top level estimate item (wl opt-in only)
      {
        name: 'sidebar.documents.outgoing.estimates.title',
        icon: 'fe fe-file',
        fn: () => this.onOpen(['documents/o/estimate']),
        visible: of(this.wls.getI('sidebar.estimate.visible')),
      },

      // Multi-level outgoing invoices (default)
      {
        name: 'sidebar.documents.outgoing.title',
        icon: 'fe fe-file-plus',
        visible: of(!this.wls.getI('sidebar.documents/o.hidden')),
        items: of([
          {
            name: 'sidebar.documents.outgoing.invoices.title',
            icon: 'fe fe-file',
            fn: () => this.onOpen(['documents/o/invoice']),
            alias: 'documents/o/invoice',
            visible: of(!this.wls.getI('sidebar.documents/o/invoice.hidden')),
          },
          {
            name: 'sidebar.documents.outgoing.estimates.title',
            icon: 'fe fe-file',
            fn: () => this.onOpen(['documents/o/estimate']),
            visible: of(!this.wls.getI('sidebar.documents/o/estimate.hidden')),
            alias: 'documents/o/estimate',
          },
          {
            name: 'sidebar.documents.outgoing.advances.title',
            icon: 'fe fe-file',
            fn: () => this.onOpen(['documents/o/advance']),
            visible: of(!this.wls.getI('sidebar.documents/o/advance.hidden')),
          },
          {
            name: 'sidebar.documents.outgoing.credit-notes.title',
            icon: 'fe fe-file',
            fn: () => this.onOpen(['documents/o/credit-note']),
            visible: of(
              !this.wls.getI('sidebar.documents/o/credit-note.hidden'),
            ),
          },
          {
            name: 'sidebar.documents.outgoing.delivery-notes.title',
            icon: 'fe fe-file',
            fn: () => this.onOpen(['documents/o/delivery-note']),
            visible: of(
              !this.wls.getI('sidebar.documents/o/delivery-note.hidden'),
            ),
          },
          {
            name: 'sidebar.documents.outgoing.write-offs.title',
            icon: 'fe fe-file',
            fn: () => this.onOpen(['documents/o/write-off']),
            class: 'pb-2',
            visible: of(!this.wls.getI('sidebar.documents/o/write-off.hidden')),
          },
          {
            name: 'sidebar.documents.outgoing.warehouse-transfers.title',
            icon: 'fe fe-file',
            fn: () => this.onOpen(['documents/o/warehouse-transfer']),
            class: 'pb-2',
            visible: of(
              !this.wls.getI('sidebar.documents/o/warehouse-transfer.hidden'),
            ).pipe(
              switchMap((visible) =>
                zip(
                  this.orgService.active.pipe(
                    switchMap((organization) =>
                      this.warehouseService
                        .get(organization.id)
                        .pipe(map((warehouses) => warehouses.totalCount > 1)),
                    ),
                  ),
                  this.hasInventory,
                ).pipe(
                  map(
                    ([hasWarehouses, hasInventory]) =>
                      visible && hasWarehouses && hasInventory,
                  ),
                ),
              ),
            ),
          },
          {
            name: 'sidebar.recurrences.title',
            icon: 'fe fe-repeat',
            fn: () => this.onOpen(['recurrences']),
            visible: of(!this.wls.getI('sidebar.recurrences.hidden')),
          },
        ]),
      },
      {
        name: 'sidebar.documents.incoming.title',
        icon: 'fe fe-file-minus',
        visible: of(!this.wls.getI('sidebar.documents/i.hidden')),
        items: of([
          {
            name: 'sidebar.documents.incoming.invoices.title',
            icon: 'fe fe-file',
            fn: () => this.onOpen(['documents/i/invoice']),
            visible: of(!this.wls.getI('sidebar.documents/i/invoice.hidden')),
          },
        ]),
      },
      {
        name: 'sidebar.orders.title',
        icon: 'fe fe-shopping-cart',
        visible: of(!this.wls.getI('sidebar.orders.hidden')).pipe(
          switchMap((notHidden) =>
            zip(
              this.hasIntegrations,
              this.orgService.active.pipe(
                filter((org) => !!org),
                switchMap((org) => this.orderService.count(org.id)),
              ),
            ).pipe(
              map(
                ([hasIntegrations, count]) =>
                  (hasIntegrations || count > 0) && notHidden,
              ),
            ),
          ),
        ),
        items: of([
          {
            name: 'sidebar.orders.all.title',
            icon: 'fe fe-list',
            fn: () => this.onOpen(['orders']),
            class: 'pb-2',
            visible: of(!this.wls.getI('sidebar.orders.all.hidden')),
          },
          {
            name: 'sidebar.orders.pending.title',
            icon: 'fe fe-clock',
            fn: () =>
              this.onOpen(
                this.sidebarService.generateLink(['orders'], {
                  status: 'processing',
                }),
              ),
            visible: of(!this.wls.getI('sidebar.orders.pending.hidden')),
          },
          {
            name: 'sidebar.orders.paid.title',
            icon: 'fe fe-dollar-sign',
            fn: () =>
              this.onOpen(
                this.sidebarService.generateLink(['orders'], {
                  paid: true,
                }),
              ),
            visible: of(!this.wls.getI('sidebar.orders.paid.hidden')),
          },
          {
            name: 'sidebar.orders.cancelled.title',
            icon: 'fe fe-slash',
            fn: () =>
              this.onOpen(
                this.sidebarService.generateLink(['orders'], {
                  cancelled: true,
                }),
              ),
            class: 'pb-2',
            visible: of(!this.wls.getI('sidebar.orders.cancelled.hidden')),
          },
          {
            name: 'sidebar.orders.card.title',
            icon: 'fe fe-credit-card',
            fn: () =>
              this.onOpen(
                this.sidebarService.generateLink(['orders'], {
                  paymentType: 'card',
                }),
              ),
            visible: of(!this.wls.getI('sidebar.orders.card.hidden')),
          },
          {
            name: 'sidebar.orders.paypal.title',
            icon: 'fe fe-credit-card',
            fn: () =>
              this.onOpen(
                this.sidebarService.generateLink(['orders'], {
                  paymentType: 'paypal',
                }),
              ),
            visible: of(!this.wls.getI('sidebar.orders.paypal.hidden')),
          },
          {
            name: 'sidebar.orders.bank.title',
            icon: 'fe fe-home',
            fn: () =>
              this.onOpen(
                this.sidebarService.generateLink(['orders'], {
                  paymentType: 'bank',
                }),
              ),
            visible: of(!this.wls.getI('sidebar.orders.bank.hidden')),
          },
          {
            name: 'sidebar.orders.cash.title',
            icon: 'fe fe-dollar-sign',
            fn: () =>
              this.onOpen(
                this.sidebarService.generateLink(['orders'], {
                  paymentType: 'cash',
                }),
              ),
            visible: of(!this.wls.getI('sidebar.orders.cash.hidden')),
          },
        ]),
      },

      // Top level client item (wl opt-in only)
      {
        name: 'sidebar.clients-and-suppliers.title',
        icon: 'fe fe-users',
        fn: () => this.onOpen(['clients']),
        visible: of(this.wls.getI('sidebar.client-and-supplier.visible')),
      },
      // Top level client item (wl opt-in) with alt. name (no suppliers)
      {
        name: 'sidebar.clients.title',
        icon: 'fe fe-users',
        fn: () => this.onOpen(['clients']),
        visible: of(this.wls.getI('sidebar.client.visible')),
      },
      // Multi-level clients (default)
      {
        name: 'sidebar.clients-and-suppliers.title',
        icon: 'fe fe-users',
        visible: of(!this.wls.getI('sidebar.clients.hidden')),
        items: of([
          {
            name: 'sidebar.clients.all.title',
            icon: 'fe fe-list',
            fn: () => this.onOpen(['clients']),
            class: 'pb-2',
            visible: of(!this.wls.getI('sidebar.clients/all.hidden')),
          },
          {
            name: 'sidebar.clients.customers.title',
            icon: 'fe fe-arrow-up-circle',
            fn: () =>
              this.onOpen(
                this.sidebarService.generateLink(['clients'], {
                  or: [{ type: 'customer' }, { type: { exists: false } }],
                }),
              ),
            visible: of(!this.wls.getI('sidebar.clients/customers.hidden')),
          },
          {
            name: 'sidebar.clients.suppliers.title',
            icon: 'fe fe-arrow-down-circle',
            fn: () =>
              this.onOpen(
                this.sidebarService.generateLink(['clients'], {
                  or: [{ type: 'supplier' }, { type: { exists: false } }],
                }),
              ),
            class: 'pb-2',
            visible: of(!this.wls.getI('sidebar.clients/suppliers.hidden')),
          },
          {
            name: 'sidebar.clients.deleted.title',
            icon: 'fe fe-trash',
            fn: () =>
              this.onOpen(
                this.sidebarService.generateLink(['clients'], {
                  deleted: true,
                }),
              ),
            visible: of(!this.wls.getI('sidebar.clients/deleted.hidden')),
          },
        ]),
      },

      // Top level items item (wl opt-in only)
      {
        name: 'sidebar.items.title',
        icon: 'fe fe-archive',
        fn: () => this.onOpen(['items']),
        visible: of(this.wls.getI('sidebar.item.visible')),
      },
      // Multi-level items (default)
      {
        name: 'sidebar.items.title',
        icon: 'fe fe-archive',
        visible: of(!this.wls.getI('sidebar.items.hidden')),
        items: of([
          {
            name: 'sidebar.items.all.title',
            icon: 'fe fe-list',
            fn: () => this.onOpen(['items']),
            class: 'pb-2',
            visible: of(!this.wls.getI('sidebar.items/all.hidden')),
          },
          {
            name: 'sidebar.items.sale.title',
            icon: 'fe fe-arrow-up-circle',
            fn: () =>
              this.onOpen(
                this.sidebarService.generateLink(['items'], {
                  or: [
                    { type: 'outgoing' },
                    { type: '' },
                    { type: { exists: false } },
                  ],
                }),
              ),
            visible: of(!this.wls.getI('sidebar.items/outgoing.hidden')),
          },
          {
            name: 'sidebar.items.purchase.title',
            icon: 'fe fe-arrow-down-circle',
            fn: () =>
              this.onOpen(
                this.sidebarService.generateLink(['items'], {
                  or: [
                    { type: 'incoming' },
                    { type: '' },
                    { type: { exists: false } },
                  ],
                }),
              ),
            class: 'pb-2',
            visible: of(!this.wls.getI('sidebar.items/incoming.hidden')),
          },
          {
            name: 'sidebar.price-lists.title',
            icon: 'fe fe-percent',
            fn: () => this.onOpen(['pricelists']),
            class: 'pb-2',
            visible: of(!this.wls.getI('sidebar.price-lists.hidden')),
          },
          {
            name: 'sidebar.items.deleted.title',
            icon: 'fe fe-trash',
            fn: () =>
              this.onOpen(
                this.sidebarService.generateLink(['items'], {
                  deleted: true,
                }),
              ),
            visible: of(!this.wls.getI('sidebar.items/deleted.hidden')),
          },
        ]),
      },

      // Top level payments item (wl opt-in only)
      {
        name: 'sidebar.payments.title',
        icon: 'fe fe-credit-card',
        fn: () => this.onOpen(['payments']),
        visible: of(this.wls.getI('sidebar.payment.visible')),
      },
      // Multi-level payments (default)
      {
        name: 'sidebar.payments.title',
        icon: 'fe fe-credit-card',
        visible: of(!this.wls.getI('sidebar.payments.hidden')),
        items: of([
          {
            name: 'sidebar.payments.all.title',
            icon: 'fe fe-list',
            fn: () => this.onOpen(['payments']),
            class: 'pb-2',
            visible: of(!this.wls.getI('sidebar.payments/all.hidden')),
          },
          {
            name: 'sidebar.payments.incoming.title',
            icon: 'fe fe-download',
            fn: () =>
              this.onOpen(
                this.sidebarService.generateLink(['payments'], {
                  amount: {
                    gt: 0,
                  },
                }),
              ),
            visible: of(!this.wls.getI('sidebar.payments/incoming.hidden')),
          },
          {
            name: 'sidebar.payments.outgoing.title',
            icon: 'fe fe-upload',
            fn: () =>
              this.onOpen(
                this.sidebarService.generateLink(['payments'], {
                  amount: {
                    lt: 0,
                  },
                }),
              ),
            visible: of(!this.wls.getI('sidebar.payments/outgoing.hidden')),
          },
          {
            name: 'sidebar.payments.this-month.title',
            icon: 'fe fe-clock',
            fn: () =>
              this.onOpen(
                this.sidebarService.generateLink(['payments'], {
                  date: {
                    between: [
                      moment.utc().startOf('month').format(),
                      moment.utc().endOf('month').format(),
                    ],
                  },
                }),
              ),
            class: 'pb-2',
            visible: of(!this.wls.getI('sidebar.payments/this-month.hidden')),
          },
          {
            name: 'sidebar.payments.deleted.title',
            icon: 'fe fe-trash',
            fn: () =>
              this.onOpen(
                this.sidebarService.generateLink(['payments'], {
                  deleted: true,
                }),
              ),
            visible: of(!this.wls.getI('sidebar.payments/deleted.hidden')),
          },
        ]),
      },
      {
        name: 'sidebar.business.title',
        icon: 'fe fe-briefcase',
        visible: of(!this.wls.getI('sidebar.business.hidden')),
        items: of([
          {
            name: 'sidebar.export.title',
            icon: 'fe fe-upload',
            fn: () => this.onOpen(['exports']),
            visible: of(!this.wls.getI('sidebar.business.export.hidden')),
          },
          {
            name: 'sidebar.import.title',
            icon: 'fe fe-download',
            fn: () => this.onOpen(['import']),
            class: 'pb-2',
            visible: of(!this.wls.getI('sidebar.business.import.hidden')),
          },
          {
            name: 'sidebar.taxes.title',
            icon: 'fe fe-percent',
            fn: () => this.onOpen(['taxes']),
            class: 'pb-2',
            visible: of(!this.wls.getI('sidebar.business.taxes.hidden')),
          },
          {
            name: 'sidebar.categories.title',
            icon: 'fe fe-pie-chart',
            fn: () => this.onOpen(['categories']),
            class: 'pb-2',
            visible: of(!this.wls.getI('sidebar.business.categories.hidden')),
          },
          {
            name: 'sidebar.units.title',
            icon: 'fe fe-map-pin',
            fn: () => this.onOpen(['units']),
            visible: of(!this.wls.getI('sidebar.business.units.hidden')).pipe(
              switchMap((notHidden) =>
                this.hasUnits.pipe(map((hasUnits) => hasUnits && notHidden)),
              ),
            ),
          },
          {
            name: 'sidebar.units.title',
            icon: 'fe fe-map-pin',
            class: 'pb-2',
            fn: () => this.featureModalService.open('units'),
            visible: of(!this.wls.getI('sidebar.business.units.hidden')).pipe(
              switchMap((notHidden) =>
                this.hasUnits.pipe(map((hasUnits) => !hasUnits && notHidden)),
              ),
            ),
          },
          {
            name: 'sidebar.warehouses.title',
            icon: 'fe fe-server',
            fn: () => this.onOpen(['warehouses']),
            visible: of(
              !this.wls.getI('sidebar.business.inventory.hidden'),
            ).pipe(
              switchMap((notHidden) =>
                this.hasInventory.pipe(
                  map((hasInventory) => hasInventory && notHidden),
                ),
              ),
            ),
          },
          {
            name: 'sidebar.warehouses.title',
            icon: 'fe fe-server',
            class: 'pb-2',
            fn: () => this.featureModalService.open('inventory'),
            visible: of(
              false && !this.wls.getI('sidebar.business.inventory.hidden'),
            ).pipe(
              switchMap((notHidden) =>
                this.hasInventory.pipe(
                  map((hasInventory) => !hasInventory && notHidden),
                ),
              ),
            ),
          },
          {
            name: 'sidebar.balance-sheet.title',
            icon: 'fe fe-activity',
            fn: () => this.onOpen(['balance-sheet']),
            class: 'pb-2',
            visible: of(
              false && !this.wls.getI('sidebar.business.balance-sheet.hidden'),
            ),
          },
        ]),
      },
      {
        name: '',
        icon: '',
        class: 'mt-auto',
        visible: of(true),
        divider: true,
      },
      {
        name: 'sidebar.whats-new.title',
        icon: 'fe fe-gift',
        fn: () => this.onOpenWhatsNewModal(),
        badge: this.languageService.current.pipe(
          switchMap((l) =>
            this.contentfuService.getByResource(l, 'help', 'new'),
          ),
          filter((data) => !!data && !!data.sys && !!data.sys.updatedAt),
          map((data) => data.sys.updatedAt),
          map((updatedAt) => {
            const lastRead = this.storateService.getItem(
              this.whatsNewCookieKey,
            );
            const notRead = moment(updatedAt).isAfter(lastRead);

            return !lastRead || notRead;
          }),
        ),
        visible: wl.pipe(map(([isWL]) => !isWL)),
      },
      {
        name: 'sidebar.help.title',
        icon: 'fe fe-help-circle',
        visible: wl.pipe(map(([isWL]) => !isWL)),
        items: of([
          {
            name: 'sidebar.helpdesk.title',
            icon: 'fe fe-life-buoy',
            visible: of(false),
            fn: () =>
              this.sidebarService.openExternalUrl(
                'http://helpdesk.getapollo.io',
              ),
          },
          {
            name: 'sidebar.blog.title',
            icon: 'fe fe-feather',
            fn: () =>
              this.sidebarService.openExternalUrl(`${this.selfUrl}/sl/blog`),
            class: 'pb-2',
            alias: 'help.blog',
          },
          {
            name: 'sidebar.live-chat.title',
            icon: 'fe fe-message-circle',
            fn: () => this.sidebarService.openChat(),
            alias: 'help.live-chat',
          },
          {
            name: 'sidebar.email-us.title',
            icon: 'fe fe-at-sign',
            fn: () =>
              this.orgService.active
                .pipe(
                  filter((org) => !!org),
                  takeUntil(this.onDestroy),
                )
                .subscribe(({ supportPin }) =>
                  this.sidebarService.emailSupport(supportPin),
                ),
            alias: 'help.email-us',
          },
          {
            name: 'sidebar.status-page.title',
            icon: 'fe fe-activity',
            fn: () =>
              this.sidebarService.openExternalUrl(
                'https://status.getapollo.io',
              ),
            class: 'pb-2',
            alias: 'help.status-page',
          },
          {
            name: 'sidebar.privacy-policy.title',
            icon: 'fe fe-lock',
            fn: () =>
              this.languageService.current
                .pipe(take(1))
                .subscribe((locale) =>
                  this.sidebarService.openExternalUrl(
                    `${this.selfUrl}/${locale}/privacy-policy`,
                  ),
                ),
            alias: 'help.privacy-policy',
          },
          {
            name: 'sidebar.terms-of-service.title',
            icon: 'fe fe-award',
            fn: () =>
              this.languageService.current
                .pipe(take(1))
                .subscribe((locale) =>
                  this.sidebarService.openExternalUrl(
                    `${this.selfUrl}/${locale}/terms-of-service`,
                  ),
                ),
            class: 'pb-2',
            alias: 'help.terms-of-service',
          },
          {
            name: 'sidebar.website.title',
            icon: 'fe fe-globe',
            fn: () =>
              this.languageService.current
                .pipe(take(1))
                .subscribe((locale) =>
                  this.sidebarService.openExternalUrl(
                    `${this.selfUrl}/${locale}/`,
                  ),
                ),
            alias: 'help.website',
          },
        ]),
      },
      {
        name: 'sidebar.support.title',
        icon: 'fe fe-life-buoy',
        visible: wl.pipe(
          map(([isWL]) => isWL && this.wls.getI('sidebar.support.URL')),
        ),
        fn: () =>
          this.languageService.current
            .pipe(take(1))
            .subscribe((locale) =>
              this.sidebarService.openExternalUrl(
                this.sidebarService.injectLocale(
                  locale,
                  this.wls.getI('sidebar.support.URL') as string,
                ),
              ),
            ),
      },
      {
        name: 'sidebar.organizations.title',
        icon: 'fe fe-grid',
        // Visible if not white lable or has more than 1 organization
        visible: activeOrganizations.pipe(
          map((orgs) => orgs.length),
          switchMap((count) =>
            notWLnotSI.pipe(map((visible) => visible || count > 1)),
          ),
        ),
        items: of([
          {
            name: 'sidebar.organizations.show-all.title',
            icon: 'fe fe-list',
            fn: () =>
              this.onOpen([
                '/organizations/choose',
                {
                  choose: true,
                  back: true,
                },
              ]),
            class: 'pb-2',
            visible: activeOrganizations.pipe(map((orgs) => orgs.length > 5)),
          },
          {
            name: 'sidebar.organizations.add.title',
            icon: 'fe fe-plus',
            fn: () =>
              this.onOpen([
                '/organizations/add',
                {
                  back: true,
                },
              ]),
            class: 'pb-2',
            alias: 'organizations.add',
            visible: notWLnotSI,
          },
          {
            name: 'sidebar.invitations.title',
            icon: 'fe fe-eye',
            fn: () =>
              this.onOpen([
                '/organizations/choose',
                {
                  back: true,
                  choose: true,
                },
              ]),
            alias: 'organizations.invitations',
            visible: this.accountService.active.pipe(
              switchMap(() =>
                this.accountService.getCurrent({
                  include: 'accountOrganizations',
                }),
              ),
              map((account) =>
                account.accountOrganizations.some(
                  (ao: AccountOrganization) =>
                    !ao.active && !ao.rejected && !ao.disabled,
                ),
              ),
            ),
            badge: of(true),
          },
        ]),
      },
      {
        name: 'sidebar.settings.title',
        icon: 'fe fe-settings',
        visible: of(!this.wls.getI('sidebar.settings.hidden')),
        items: of([
          {
            name: 'sidebar.subscription.title',
            icon: 'fe fe-heart',
            fn: () => this.onOpen(['subscriptions/plans']),
            visible: notWLnotSI,
          },
          {
            name: 'sidebar.organization-settings.title',
            icon: 'fe fe-home',
            fn: () => this.onOpen(['settings/organization']),
            visible: of(!this.wls.getI('sidebar.settings/organization.hidden')),
          },
          {
            name: 'sidebar.customizations.title',
            icon: 'fe fe-layout',
            fn: () => this.onOpen(['settings/customizations']),
            visible: of(
              !this.wls.getI('sidebar.settings/customizations.hidden'),
            ),
          },
          {
            name: 'sidebar.access.title',
            icon: 'fe fe-user-plus',
            fn: () => this.onOpen(['settings/access']),
            visible: of(!this.wls.getI('sidebar.settings/access.hidden')),
            disabled: isAdmin.pipe(map((isAdmin) => !isAdmin)),
          },
          {
            name: 'sidebar.payment-processing.title',
            icon: 'fe fe-credit-card',
            fn: () => this.onOpen(['settings/payment-processing']),
            visible: combineLatest([
              of(!this.wls.getI('sidebar.settings/payment-processing.hidden')),
              stripeConnectActive,
            ]).pipe(map(([wlI, active]) => wlI && active)),
            disabled: isAdmin.pipe(map((isAdmin) => !isAdmin)),
          },
          {
            name: 'sidebar.integrations.title',
            icon: 'fe fe-zap',
            fn: () => this.onOpen(['settings/integrations']),
            visible: of(!this.wls.getI('sidebar.settings/integrations.hidden')),
            disabled: isAdmin.pipe(map((isAdmin) => !isAdmin)),
          },
          {
            name: 'sidebar.payment-processing.title',
            icon: 'fe fe-credit-card',
            fn: () => this.onOpen(['settings/payment-processing']),
            visible: paymentProcessingActive,
            disabled: isAdmin.pipe(map((isAdmin) => !isAdmin)),
          },
          {
            name: 'sidebar.furs.title',
            icon: 'fe fe-upload-cloud',
            fn: () => this.onOpen(['settings/furs']),
            visible: combineLatest([
              of(this.wls.getI('sidebar.settings/furs.hidden')),
              this.orgService.active,
            ]).pipe(
              filter(([_, org]) => !!org),
              map(([hidden, org]) => !hidden && org.countryAlpha2Code === 'SI'),
            ),
          },
          {
            name: 'sidebar.fina.title',
            icon: 'fe fe-upload-cloud',
            fn: () => this.onOpen(['settings/fina']),
            visible: combineLatest([
              of(this.wls.getI('sidebar.settings/fina.hidden')),
              this.orgService.active,
            ]).pipe(
              filter(([_, org]) => !!org),
              map(([hidden, org]) => !hidden && org.countryAlpha2Code === 'HR'),
            ),
          },
          // {
          //   name: 'sidebar.dark-mode.title',
          //   icon: 'fe fe-moon',
          //   fn: () => this.themeService.toggleDark(),
          //   class: 'pb-2',
          //   visible: this.themeService.dark.pipe(
          //     map((isDark) => !isDark),
          //     map(
          //       (notDark) =>
          //         notDark &&
          //         !this.wls.getI('sidebar.settings.dark-mode.hidden'),
          //     ),
          //   ),
          // },
          // {
          //   name: 'sidebar.light-mode.title',
          //   icon: 'fe fe-sun',
          //   fn: () => this.themeService.toggleDark(),
          //   class: 'pb-2',
          //   visible: this.themeService.dark.pipe(
          //     map(
          //       (isDark) =>
          //         isDark && !this.wls.getI('sidebar.settings.dark-mode.hidden'),
          //     ),
          //   ),
          // },
          {
            name: 'sidebar.language.title',
            icon: 'fe fe-flag',
            visible: of(!this.wls.getI('sidebar.language.hidden')),
            items: of(
              Object.keys(languages).map((key) => {
                const l = languages[key];
                const nameLower = l.name.toLowerCase();

                return {
                  name: `sidebar.language.${nameLower}.title`,
                  icon: 'fe fe-flag',
                  fn: () => this.onChangeLanguage(l.symbol),
                  visible: of(
                    !this.wls.getI(`sidebar.language.${nameLower}.hidden`) &&
                      !l.hidden,
                  ),
                };
              }),
            ),
          },
        ]),
      },
      {
        name: 'sidebar.account.title',
        icon: 'fe fe-user',
        visible: of(!this.wls.getI('sidebar.account.hidden')),
        items: of([
          {
            name: 'sidebar.referral.title',
            icon: 'fe fe-dollar-sign',
            fn: () => this.onOpen(['referral']),
            class: 'pb-2',
            visible: wl.pipe(map(([isWL]) => !isWL)),
          },
          {
            name: 'sidebar.account.settings.title',
            icon: 'fe fe-settings',
            fn: () => this.onOpen(['settings/account']),
            class: 'pb-2',
            visible: of(!this.wls.getI('sidebar.account.settings.hidden')),
          },
          {
            name: 'sidebar.logout.title',
            fn: () => this.onLogout(),
            icon: 'fe fe-power',
            visible: wl.pipe(
              map(([isWL]) => !isWL),
              switchMap((visible) =>
                this.loggingOut
                  .asObservable()
                  .pipe(map((lo) => !lo && visible)),
              ),
            ),
          },
          {
            name: 'sidebar.logout.title',
            icon: 'fal fa-spinner-third fa-spin',
            visible: wl.pipe(
              map(([isWL]) => !isWL),
              switchMap((visible) =>
                this.loggingOut.asObservable().pipe(map((lo) => lo && visible)),
              ),
            ),
          },
        ]),
      },
    ];

    this.items$ = activeOrganizations.pipe(
      switchMap((organizations) =>
        this.orgService.active.pipe(
          filter((org) => !!org),
          map((active) => [active, organizations]),
        ),
      ),
      filter(([active]) => !!active),
      map(([active, organizations]: [Organization, Organization[]]) => {
        const idx = organizations.findIndex((o) => o.id === active.id);

        return [
          ...organizations.slice(idx, idx + 1),
          ...organizations.slice(0, idx),
          ...organizations.slice(idx + 1),
        ].slice(0, 5);
      }),
      map((organizations) => {
        const organizationsItems: SidebarItem[] = organizations.map(
          (o, idx) => {
            // const first = idx === 0;
            // const classA = first ? 'active' : '';

            return {
              name: o.name,
              icon: '',
              alias: `organizations.${o.id}`,
              fn: () => this.onOpen([o.id, 'dashboard'], true, false),
              class: 'pb-2',
              // class_a: classA,
            };
          },
        );

        const organization = this.items.find(
          (el) => el.name === 'sidebar.organizations.title',
        );

        organization.items = organization.items.pipe(
          map((items) => [...organizationsItems, ...items]),
        );

        return this.items;
      }),
    );

    this.onResize();
  }

  onChangeLanguage(lang: string): void {
    this.accountService.active
      .pipe(
        take(1),
        switchMap(({ id }) =>
          this.accountService.patch(id, { locale: lang }, false),
        ),
      )
      .subscribe(
        () => {},
        () => {},
      );

    this.languageService.setCurrent(lang);

    this.onToggle(null);

    // this.cdr.markForCheck();
  }

  onClick(): void {
    if (this.mobile) {
      this.mobileVisibilityState = 'hidden';
      // Toggle all including parent which would not be toggled if not mobile
      this.onToggle(null);
    }
  }

  onLogoClick(): void {
    this.mobileVisibilityState =
      this.mobileVisibilityState === 'visible' ? 'hidden' : 'visible';
  }

  onLogout(): void {
    this.loggingOut.next(true);
    this.accountService
      .logout()
      .subscribe(
        () => {},
        () => {},
      )
      .add(() => {
        this.router.navigate(['/login']);
        this.loggingOut.next(false);
        this.loggingOut.complete();
      });
  }

  onOpenWhatsNewModal(): void {
    this.helpModalService.open('new');
    this.storateService.setItem(
      this.whatsNewCookieKey,
      moment().format(),
      moment().add(1, 'year').toDate(),
    );
  }

  onResize(): void {
    const screenWidth = window.innerWidth;
    this.mobile = screenWidth < 576;
    this.onToggleMobile(1500);
  }

  onToggleMobile(d: number): void {
    of(this.mobile)
      .pipe(delay(this.mobile ? d : 0))
      .subscribe(() => {
        this.mobileVisibilityState = this.mobile ? 'hidden' : 'visible';
      })
      .add(() => this.cdr.markForCheck());
  }

  onToggle(item: SidebarItem): void {
    this.items = [
      ...this.items.map((i) => {
        if (i !== item && i.items) {
          i.expanded = false;
          i.active = false;
        }

        return Object.assign({}, i);
      }),
    ];

    if (item && item.items) {
      this.mobileVisibilityState = 'visible';
    }

    if (item && item.expanded) {
      item.active = true;
    }

    this.items$ = of(this.items);
  }

  onOpen(path: any[], navigate = true, relative = true): void {
    this.lastClicked = path[0];

    if (navigate) {
      const options: NavigationExtras = {};

      if (relative) {
        options.relativeTo = this.route;
      }

      this.router.navigate(path, options);
    }
  }

  trackByAlias(_: number, item: any): string {
    return item.alias;
  }
}
